import { LocationQueryRaw, NavigationFailure, RouteLocationRaw, useRouter } from 'vue-router';

import { AppRouteNames } from './app-routes.enum';
import {
  companyOnboardingRoute,
  confirmResetPasswordRoute,
  emailConfirmationRoute,
  missingPasswordSignInRoute,
  oneTimeCodeSignInRoute,
  portfolioRoute,
  propertyDetailsRoute,
  propertyRoute,
  propertyDashboardRoute,
  renterProfileRoute,
  resetPasswordRoute,
  showingDetailsRoute,
  signInRoute,
  signUpRoute,
  renterApplicationReviewRoute,
  renterTransUnionReportRoute,
} from './navigation-routes';
import { PropertyOnboardingType } from './property-onboarding.enum';

type NavigationResult = Promise<NavigationFailure | void | undefined>;

export function useNavigation() {
  const router = useRouter();

  const goBack = (fallbackRoute?: RouteLocationRaw) => {
    const hasPreviousPageToNavigateTo = window.history.state?.back;

    if (hasPreviousPageToNavigateTo) {
      router.back(); // if there is a history, go back
    } else if (fallbackRoute) {
      router.replace(fallbackRoute); // if there is no history (e.g. url opened from the link), go to the fallback route
    } else {
      router.replace('/'); // if there is no history and no fallback route, go to the home page
    }
  };

  const openSignIn = async (): NavigationResult => {
    return router.push(signInRoute());
  };

  const openSignUp = async (): NavigationResult => {
    return router.push(signUpRoute());
  };

  const openCompanyOnboarding = async (queryParams?: LocationQueryRaw): NavigationResult => {
    return router.push(companyOnboardingRoute(queryParams));
  };

  const openEmailConfirmation = async (queryParams?: LocationQueryRaw): NavigationResult => {
    return router.push(emailConfirmationRoute(queryParams));
  };

  const openMissingPasswordSignIn = async (queryParams: { email: string }): NavigationResult => {
    return router.push(missingPasswordSignInRoute(queryParams));
  };

  const openOneTimeCodeSignIn = async (queryParams: { email: string }): NavigationResult => {
    return router.push(oneTimeCodeSignInRoute(queryParams));
  };

  const openPasswordReset = async (queryParams: { email: string; sent: string }): NavigationResult => {
    return router.push(resetPasswordRoute(queryParams));
  };

  const openPasswordResetConfirmation = async (): NavigationResult => {
    return router.push(confirmResetPasswordRoute());
  };

  const openNewPropertyOnboarding = async (): NavigationResult => {
    return router.push({
      name: AppRouteNames.INVESTOR_PROPERTY_ONBOARDING_OVERVIEW,
      params: { propertyId: PropertyOnboardingType.NewProperty },
    });
  };

  const openShowingDetails = async (showingId: string, replaceRoute = false): NavigationResult => {
    const route = showingDetailsRoute(showingId);

    return replaceRoute ? router.replace(route) : router.push(route);
  };

  const openPortfolio = async (): NavigationResult => {
    return router.push(portfolioRoute());
  };

  const openProperty = async (propertyId: string): NavigationResult => {
    return router.push(propertyRoute(propertyId));
  };

  const openPropertyDashboard = async (propertyId: string): NavigationResult => {
    return router.push(propertyDashboardRoute(propertyId));
  };

  const openPropertyDetails = async (propertyId: string): NavigationResult => {
    return router.push(propertyDetailsRoute(propertyId));
  };

  const openRenterProfile = async (propertyId: string, renterId: string): NavigationResult => {
    return router.push(renterProfileRoute(propertyId, renterId));
  };

  const openRenterApplicationReview = async (
    propertyId: string,
    renterId: string,
    applicationId: string,
  ): NavigationResult => {
    return router.push(renterApplicationReviewRoute(propertyId, renterId, applicationId));
  };

  const openRenterTransUnionReportRoute = async (applicationId: string, reportType: string): NavigationResult => {
    return router.push(renterTransUnionReportRoute(applicationId, reportType));
  };

  return {
    goBack,

    openShowingDetails,
    openSignIn,
    openSignUp,
    openEmailConfirmation,
    openMissingPasswordSignIn,
    openOneTimeCodeSignIn,
    openPasswordReset,
    openPasswordResetConfirmation,
    openNewPropertyOnboarding,
    openCompanyOnboarding,
    openProperty,
    openPropertyDashboard,
    openPropertyDetails,
    openRenterProfile,
    openPortfolio,
    openRenterApplicationReview,
    openRenterTransUnionReportRoute,
  };
}
